import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { VisitTimeRow } from "../../types/pivot.type";

dayjs.extend(utc);
dayjs.extend(timezone);

/** Route times are branch wall-clock times — pinned to Bangkok, whatever the viewer's zone. */
export const ROUTE_TIME_TZ = "Asia/Bangkok";

/** ISO instant → `HH:mm` in Bangkok; "–" when absent or unparseable. */
export const fmtClock = (iso: string | null | undefined): string => {
  if (!iso) return "–";
  const d = dayjs(iso).tz(ROUTE_TIME_TZ);
  return d.isValid() ? d.format("HH:mm") : "–";
};

/** Minutes past local midnight in Bangkok, or null. */
export const minuteOfDay = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const d = dayjs(iso).tz(ROUTE_TIME_TZ);
  if (!d.isValid()) return null;
  return d.hour() * 60 + d.minute() + d.second() / 60;
};

/** `12.3 น.` — one decimal, the unit every minute figure on these screens carries. */
export const fmtMinutes = (v: number | null | undefined): string =>
  v === null || v === undefined || Number.isNaN(v) ? "–" : `${v.toFixed(1)} น.`;

/** `850 ม.` under a kilometre, `2.3 กม.` from one up. */
export const fmtDistance = (metres: number | null | undefined): string => {
  if (metres === null || metres === undefined || Number.isNaN(metres)) return "–";
  if (metres < 1000) return `${Math.round(metres)} ม.`;
  return `${(metres / 1000).toFixed(1)} กม.`;
};

/** `07:26–07:41`, or "–" when either end is missing. */
export const fmtClockRange = (from: string | null, to: string | null): string =>
  from && to ? `${from}–${to}` : "–";

/**
 * Lookup from a store row to its PWA visit-time row.
 *
 * Keyed on `customer_code` + `route_code` first: a store moved between routes inside the
 * range can come back once per route, and the สายส่ง tab shows it under a specific one. When
 * the route does not match (or is not given) the store's own row is used, and if it has
 * several the one with the most visit days wins. Stores with no PWA visit are simply absent,
 * so the caller renders "–".
 */
export const buildVisitTimeLookup = (rows: VisitTimeRow[]) => {
  const byStoreRoute = new Map<string, VisitTimeRow>();
  const byStore = new Map<string, VisitTimeRow>();
  rows.forEach((r) => {
    byStoreRoute.set(`${r.customer_code}|${r.route_code}`, r);
    const prev = byStore.get(r.customer_code);
    if (!prev || r.visit_day_count > prev.visit_day_count) byStore.set(r.customer_code, r);
  });
  return (customerCode: string, routeCode?: string | null): VisitTimeRow | undefined =>
    (routeCode ? byStoreRoute.get(`${customerCode}|${routeCode}`) : undefined) ??
    byStore.get(customerCode);
};

/**
 * Minutes from local midnight at the start of the route-day `date` (`YYYY-MM-DD`) — past
 * 24 × 60 when a count starts after midnight, so a late return does not wrap to the morning.
 */
export const minutesFromDayStart = (
  iso: string | null | undefined,
  date: string,
): number | null => {
  if (!iso) return null;
  const d = dayjs(iso);
  if (!d.isValid()) return null;
  const start = dayjs.tz(date, ROUTE_TIME_TZ).startOf("day");
  return d.diff(start, "minute", true);
};

/** `HH:mm` for a minute offset from local midnight (wraps past 24 h). */
export const fmtMinuteOfDay = (m: number): string => {
  const total = Math.floor(m);
  const h = Math.floor(total / 60) % 24;
  const mm = total % 60;
  return `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
};
